import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, Film, Lightbulb, Loader2, Pause, Play } from 'lucide-react';
import Sheet from './Sheet';
import { Chip } from './parts';
import { hasDemoContent, prefersReducedMotion } from './playerLogic';

// «Ver como se faz»: a demonstração do exercício POR CIMA do player.
//
// Não desmonta o player (a série que se estava a editar fica como estava) e fechar devolve o foco ao botão que o abriu. Recebe uma
// demonstração já normalizada (ver demoRegistry.resolveDemo): a media que existir (vídeo, animação, fotografia), os passos, os erros comuns,
// os músculos e o equipamento. Sem media não bloqueia nada: mostra o que houver, ou diz que ainda não há — e o treino segue.
//
// Vídeo: arranca sozinho, mudo e em ciclo (a não ser que o sistema peça "movimento reduzido": então mostra o póster e um botão de reproduzir),
// com um botão de pausa/reprodução. Pára quando o ecrã deixa de estar à vista. Se não carregar, sai da lista e o sócio fica com a fotografia e
// as instruções — nunca com uma caixa partida.

function DemoVideo({ item, title, active, onFail }) {
  const ref = useRef(null);
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const [state, setState] = useState('loading');   // loading | playing | paused
  useEffect(() => { setState('loading'); }, [item.url]);

  const start = useCallback(() => {
    const v = ref.current;
    if (!v || typeof v.play !== 'function') return;
    try {
      const result = v.play();
      if (result && typeof result.catch === 'function') result.catch(() => setState('paused'));   // o navegador recusou o arranque sozinho: fica o botão
    } catch { setState('paused'); }
  }, []);

  // À vista e sem "movimento reduzido": arranca. Fora de vista: pára.
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    if (active && !reduced) start();
    else { try { v.pause(); } catch { /* sem pause */ } setState('paused'); }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { try { v.pause(); } catch { /* idem */ } } else if (active && !reduced) start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); try { v.pause(); } catch { /* idem */ } };
  }, [active, reduced, item.url, start]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) start(); else v.pause();
  };

  return (
    <div className="wp-demo-video" data-state={state}>
      <video ref={ref} className="wp-demo-el" src={item.url} poster={item.poster || undefined} playsInline loop muted preload="auto"
        aria-label={item.alt || `Demonstração de ${title}`} data-testid="demo-video"
        onPlaying={() => setState('playing')} onPause={() => setState('paused')} onWaiting={() => setState('loading')}
        onLoadedData={() => setState((s) => (s === 'loading' && ref.current && ref.current.paused ? 'paused' : s))}
        onError={() => onFail(item.url)} />
      {state === 'loading' && !reduced ? <span className="wp-demo-loading" role="status"><Loader2 size={28} className="wp-spin" aria-hidden="true" /><span className="wp-visually-hidden">A carregar o vídeo…</span></span> : null}
      <button type="button" className={`wp-demo-play ${state === 'playing' ? 'is-playing' : ''}`} onClick={toggle} data-testid="demo-toggle"
        aria-label={state === 'playing' ? 'Pausar o vídeo' : 'Reproduzir o vídeo'}>
        {state === 'playing' ? <Pause size={26} aria-hidden="true" /> : <Play size={26} aria-hidden="true" />}
      </button>
    </div>
  );
}

function DemoMedia({ item, title, active, onFail }) {
  if (item.kind === 'video') return <DemoVideo item={item} title={title} active={active} onFail={onFail} />;
  if (item.kind === 'embed') return <DemoEmbed item={item} title={title} />;
  return <img className="wp-demo-el" src={item.url} alt={item.alt || `Demonstração de ${title}`} draggable={false} onError={() => onFail(item.url)} data-testid="demo-image" />;
}

// Vídeo incorporado (YouTube): o player OFICIAL, dentro do ecrã, em youtube-nocookie.com e SEM autoplay. Não há ficheiro
// nosso nem cópia nenhuma — quem serve o vídeo é o YouTube.
//
// Sem rede o iframe não carrega e ficaria um quadro vazio (ou um erro do browser): por isso, quando o aparelho diz que está
// offline, ou quando o iframe não deu sinal de vida em poucos segundos, mostra-se uma frase curta e o endereço do vídeo —
// nunca um ecrã em branco. E o resto do treino continua: nada aqui bloqueia séries, registo ou descanso.
const EMBED_TIMEOUT_MS = 6000;

function DemoEmbed({ item, title }) {
  const offlineAtStart = typeof navigator !== 'undefined' && navigator.onLine === false;
  const [state, setState] = useState(offlineAtStart ? 'offline' : 'loading');   // loading | ready | offline
  useEffect(() => { setState(offlineAtStart ? 'offline' : 'loading'); }, [item.url, offlineAtStart]);

  // O iframe não avisa quando falha; o que se pode medir é «carregou em tempo». Sem sinal, cai para o endereço.
  useEffect(() => {
    if (state !== 'loading') return undefined;
    const timer = setTimeout(() => setState((s) => (s === 'loading' ? 'offline' : s)), EMBED_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state, item.url]);

  const address = item.watchUrl || item.url;
  const channel = item.channel ? `${item.channel} · ` : '';

  if (state === 'offline') {
    return (
      <div className="wp-demo-offline" role="status" data-testid="demo-embed-offline">
        <p className="wp-demo-offline-title">Não foi possível mostrar o vídeo do YouTube.</p>
        <p className="wp-muted">Este vídeo é de terceiros e vem da Internet. O treino continua normalmente.</p>
        <p className="wp-demo-offline-address">{address}</p>
        {address && /^https:\/\//i.test(address)
          ? <a className="wp-btn wp-btn-ghost wp-external" href={address} target="_blank" rel="noopener noreferrer">Abrir no YouTube (nova janela)</a>
          : null}
      </div>
    );
  }

  return (
    <div className="wp-demo-video" data-state={state} data-testid="demo-embed">
      <iframe
        className="wp-demo-el wp-demo-frame"
        src={item.url}
        title={item.alt ? `Demonstração de ${title}: ${item.alt}` : `Demonstração de ${title} (YouTube)`}
        data-testid="demo-embed-frame"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
        onLoad={() => setState('ready')}
      />
      <p className="wp-demo-credit wp-demo-embed-credit" data-testid="demo-embed-credit">
        {channel}Vídeo do YouTube · conteúdo de terceiros, incorporado.
      </p>
    </div>
  );
}


export default function DemoOverlay({ open, demo, onClose, stacked = false }) {
  const [current, setCurrent] = useState(0);
  const [failed, setFailed] = useState(() => new Set());
  useEffect(() => { if (open) { setCurrent(0); setFailed(new Set()); } }, [open, demo?.title]);
  const onFail = useCallback((url) => setFailed((s) => (s.has(url) ? s : new Set(s).add(url))), []);
  if (!open || !demo) return null;

  const media = demo.media.filter((m) => !failed.has(m.url));
  const shown = media[Math.min(current, media.length - 1)];
  const lostSome = failed.size > 0;
  const primary = demo.muscles.filter((m) => m.role === 'primary');
  const secondary = demo.muscles.filter((m) => m.role !== 'primary');
  const hasContent = hasDemoContent(demo);
  const label = (m) => (m.kind === 'video' ? 'Vídeo' : m.kind === 'embed' ? 'Vídeo do YouTube' : m.kind === 'animation' ? 'Animação' : 'Imagem');

  return (
    <Sheet open={open} onClose={onClose} title="Ver como se faz" subtitle={demo.title} variant="demo" className={stacked ? 'is-stacked' : ''} testId="demo-overlay"
      footer={<button type="button" className="wp-btn wp-btn-primary" onClick={onClose} data-autofocus data-testid="demo-close">Voltar ao treino</button>}>
      {media.length > 0 ? (
        <>
          <div className="wp-demo-stage">
            <DemoMedia key={shown.url} item={shown} title={demo.title} active={open} onFail={onFail} />
            {media.length > 1 ? (
              <div className="wp-demo-dots" role="tablist" aria-label="Ficheiros da demonstração">
                {media.map((m, i) => (
                  <button key={m.url} type="button" role="tab" aria-selected={m === shown} className={`wp-dot ${m === shown ? 'is-on' : ''}`} onClick={() => setCurrent(i)}
                    aria-label={`${label(m)} ${i + 1} de ${media.length}`} />
                ))}
              </div>
            ) : null}
          </div>
          {shown.origin === 'ai' && (shown.kind === 'video' || shown.kind === 'animation')
            ? <p className="wp-demo-ai" data-testid="demo-ai-badge"><span className="wp-ai-badge">{shown.originLabel || 'Gerado por IA'}</span></p> : null}
          {shown.attribution && shown.kind !== 'embed' ? <p className="wp-demo-credit" data-testid="demo-credit">{shown.attribution}</p> : null}
          {lostSome ? <p className="wp-note" role="status" data-testid="demo-lost">Um dos ficheiros não carregou; fica o que está disponível.</p> : null}
        </>
      ) : (
        <div className="wp-demo-empty" data-testid="demo-empty">
          <Film size={34} aria-hidden="true" />
          <p className="wp-demo-empty-title">{lostSome ? 'Não foi possível carregar a demonstração' : 'Ainda não há demonstração em vídeo ou imagem'}</p>
          <p className="wp-muted">{hasContent ? 'Pode seguir as instruções abaixo.' : 'Pode continuar o treino: quando o ginásio a acrescentar, aparece aqui.'}</p>
        </div>
      )}

      {demo.externalVideoUrl ? (
        <a className="wp-btn wp-btn-ghost wp-external" href={demo.externalVideoUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} aria-hidden="true" /> Abrir vídeo externo (nova janela)</a>
      ) : null}

      {demo.muscles.length > 0 || demo.equipment.length > 0 ? (
        <div className="wp-demo-block">
          {primary.length > 0 ? (<div className="wp-chip-row"><span className="wp-chip-label">Músculos principais</span>{primary.map((m) => <Chip key={m.id} tone="accent">{m.name}</Chip>)}</div>) : null}
          {secondary.length > 0 ? (<div className="wp-chip-row"><span className="wp-chip-label">Também trabalha</span>{secondary.map((m) => <Chip key={m.id}>{m.name}</Chip>)}</div>) : null}
          {demo.equipment.length > 0 ? (<div className="wp-chip-row"><span className="wp-chip-label">Equipamento</span>{demo.equipment.map((e) => <Chip key={e.id} tone={e.isAvailable === false ? 'warn' : 'plain'}>{e.name}{e.isAvailable === false ? ' · indisponível' : ''}</Chip>)}</div>) : null}
        </div>
      ) : null}

      {demo.steps.length > 0 ? (
        <div className="wp-demo-block">
          <h3 className="wp-h3">Como fazer</h3>
          <ol className="wp-steps">{demo.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </div>
      ) : null}

      {demo.mistakes.length > 0 ? (
        <div className="wp-demo-block wp-mistakes">
          <h3 className="wp-h3"><AlertTriangle size={16} aria-hidden="true" /> Erros comuns</h3>
          <ul className="wp-list">{demo.mistakes.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      ) : null}

      {demo.tip ? <p className="wp-tip"><Lightbulb size={16} aria-hidden="true" /> {demo.tip}</p> : null}
    </Sheet>
  );
}
